<script lang="ts">
    import { t } from '../stores/locale';
    import Button      from '../components/forms/Button.svelte';
    import SearchInput from '../components/forms/SearchInput.svelte';
    import FilterChips from '../components/forms/FilterChips.svelte';
    import Select      from '../components/forms/Select.svelte';
    import Icon        from '../components/Icon.svelte';
    import type { IconName } from '../components/Icon.svelte';

    type LogLevel = 'error' | 'warning' | 'info' | 'success';

    interface LogEntry {
        id:      number;
        level:   LogLevel;
        message: string;
        detail:  string;
        ts:      string;
    }

    // ── Mock data (50 entries) ────────────────────────────────────────────────

    const ALL_LOGS: LogEntry[] = [
        { id: 50, level: 'success', message: 'Fire alarm cleared',   detail: 'Sector B — all sensors back within range',        ts: '2026-03-22 10:08' },
        { id: 49, level: 'error',   message: 'Fire detected',        detail: 'Sector B — smoke + heat threshold exceeded',      ts: '2026-03-22 10:05' },
        { id: 48, level: 'warning', message: 'Temperature high',     detail: 'Sector C — 78 °C, threshold is 75 °C',            ts: '2026-03-22 09:56' },
        { id: 47, level: 'warning', message: 'Missing sensor',       detail: 'Sensor ID 0x2F not responding',                   ts: '2026-03-22 09:39' },
        { id: 46, level: 'info',    message: 'User login',           detail: 'Admin logged in from 192.168.1.42',               ts: '2026-03-22 09:23' },
        { id: 45, level: 'info',    message: 'System update',        detail: 'Firmware 2.4.1 installed successfully',           ts: '2026-03-22 09:10' },
        { id: 44, level: 'success', message: 'Sensor reconnected',   detail: 'Sensor ID 0x1A back online after 3 min',         ts: '2026-03-22 08:47' },
        { id: 43, level: 'warning', message: 'Low battery',          detail: 'Sensor ID 0x1A — battery at 12 %',               ts: '2026-03-22 08:44' },
        { id: 42, level: 'info',    message: 'Config saved',         detail: 'Zone thresholds updated by admin',                ts: '2026-03-22 07:30' },
        { id: 41, level: 'info',    message: 'System reboot',        detail: 'Scheduled maintenance reboot completed',          ts: '2026-03-22 07:10' },
        { id: 40, level: 'error',   message: 'Sensor failure',       detail: 'Sensor ID 0x3C — hardware fault reported',       ts: '2026-03-22 06:55' },
        { id: 39, level: 'success', message: 'Sensor replaced',      detail: 'Sensor ID 0x3C replaced and online',             ts: '2026-03-22 06:30' },
        { id: 38, level: 'info',    message: 'Backup completed',     detail: 'Config backup written to /backup/2026-03-22',     ts: '2026-03-22 06:00' },
        { id: 37, level: 'warning', message: 'High humidity',        detail: 'Sector A — humidity at 91 %, threshold 85 %',    ts: '2026-03-22 05:42' },
        { id: 36, level: 'success', message: 'Humidity normalised',  detail: 'Sector A — humidity back at 78 %',               ts: '2026-03-22 05:15' },
        { id: 35, level: 'info',    message: 'User logout',          detail: 'Admin session ended',                             ts: '2026-03-22 04:58' },
        { id: 34, level: 'info',    message: 'Scheduled test',       detail: 'Weekly sensor ping test completed OK',           ts: '2026-03-22 04:00' },
        { id: 33, level: 'warning', message: 'Comms delay',          detail: 'Sector C gateway response > 2 s',                ts: '2026-03-22 03:17' },
        { id: 32, level: 'info',    message: 'NTP sync',             detail: 'Clock synchronised with pool.ntp.org',            ts: '2026-03-22 03:00' },
        { id: 31, level: 'success', message: 'Comms restored',       detail: 'Sector C gateway latency normal',                 ts: '2026-03-22 02:51' },
        { id: 30, level: 'error',   message: 'Power fluctuation',    detail: 'UPS event — mains dip detected',                  ts: '2026-03-22 02:33' },
        { id: 29, level: 'success', message: 'Power stable',         detail: 'Mains supply restored to normal',                 ts: '2026-03-22 02:34' },
        { id: 28, level: 'info',    message: 'User login',           detail: 'Operator logged in from 192.168.1.11',            ts: '2026-03-22 01:20' },
        { id: 27, level: 'info',    message: 'Zone renamed',         detail: 'Zone 4 renamed to "Storage East"',               ts: '2026-03-22 01:05' },
        { id: 26, level: 'info',    message: 'User logout',          detail: 'Operator session ended',                          ts: '2026-03-22 00:58' },
        { id: 25, level: 'warning', message: 'Temperature high',     detail: 'Sector A — 72 °C, threshold is 70 °C',            ts: '2026-03-21 23:44' },
        { id: 24, level: 'success', message: 'Temperature normal',   detail: 'Sector A — back within range at 68 °C',          ts: '2026-03-21 23:31' },
        { id: 23, level: 'info',    message: 'Config saved',         detail: 'Notification recipients updated',                 ts: '2026-03-21 22:15' },
        { id: 22, level: 'warning', message: 'Low disk space',       detail: 'Log partition at 88 %, threshold 85 %',          ts: '2026-03-21 21:00' },
        { id: 21, level: 'info',    message: 'Log rotation',         detail: 'Old logs archived, disk at 62 %',                ts: '2026-03-21 21:05' },
        { id: 20, level: 'info',    message: 'System update',        detail: 'Firmware 2.4.0 installed successfully',           ts: '2026-03-21 20:00' },
        { id: 19, level: 'info',    message: 'System reboot',        detail: 'Post-update reboot completed',                    ts: '2026-03-21 20:05' },
        { id: 18, level: 'error',   message: 'Auth failure',         detail: '3 failed login attempts from 10.0.0.99',          ts: '2026-03-21 19:22' },
        { id: 17, level: 'info',    message: 'IP blocked',           detail: '10.0.0.99 blocked for 30 minutes',               ts: '2026-03-21 19:22' },
        { id: 16, level: 'success', message: 'Backup completed',     detail: 'Config backup written to /backup/2026-03-21',     ts: '2026-03-21 18:00' },
        { id: 15, level: 'warning', message: 'Missing sensor',       detail: 'Sensor ID 0x11 not responding',                   ts: '2026-03-21 17:30' },
        { id: 14, level: 'success', message: 'Sensor reconnected',   detail: 'Sensor ID 0x11 back online',                     ts: '2026-03-21 17:36' },
        { id: 13, level: 'info',    message: 'User login',           detail: 'Admin logged in from 192.168.1.42',               ts: '2026-03-21 16:45' },
        { id: 12, level: 'info',    message: 'Zone added',           detail: 'Zone 5 "Roof Access" created',                   ts: '2026-03-21 16:50' },
        { id: 11, level: 'info',    message: 'User logout',          detail: 'Admin session ended',                             ts: '2026-03-21 16:30' },
        { id: 10, level: 'warning', message: 'High humidity',        detail: 'Sector B — humidity at 89 %',                    ts: '2026-03-21 14:12' },
        { id:  9, level: 'success', message: 'Humidity normalised',  detail: 'Sector B — humidity back at 74 %',               ts: '2026-03-21 13:55' },
        { id:  8, level: 'info',    message: 'NTP sync',             detail: 'Clock synchronised with pool.ntp.org',            ts: '2026-03-21 12:00' },
        { id:  7, level: 'info',    message: 'Scheduled test',       detail: 'Daily sensor ping test completed OK',            ts: '2026-03-21 10:00' },
        { id:  6, level: 'error',   message: 'Fire detected',        detail: 'Sector A — smoke sensor triggered',               ts: '2026-03-21 09:02' },
        { id:  5, level: 'success', message: 'Fire alarm cleared',   detail: 'Sector A — all sensors back within range',       ts: '2026-03-21 09:18' },
        { id:  4, level: 'info',    message: 'System reboot',        detail: 'Scheduled maintenance reboot completed',          ts: '2026-03-21 07:00' },
        { id:  3, level: 'info',    message: 'Config saved',         detail: 'Initial zone configuration applied',              ts: '2026-03-21 06:45' },
        { id:  2, level: 'info',    message: 'System update',        detail: 'Firmware 2.3.9 installed',                        ts: '2026-03-21 06:30' },
        { id:  1, level: 'info',    message: 'System start',         detail: 'Device booted and all sensors initialised',       ts: '2026-03-21 06:10' },
    ];

    // ── Filter / search state ─────────────────────────────────────────────────

    const LEVELS: LogLevel[] = ['error', 'warning', 'info', 'success'];
    const LEVEL_OPTIONS = LEVELS.map(l => ({ value: l, label: $t(`logs.level.${l}`) }));

    let search      = '';
    let activeFilters = new Set<LogLevel>();

    function toggleFilter(lvl: string) {
        if (activeFilters.has(lvl as LogLevel)) activeFilters.delete(lvl as LogLevel);
        else activeFilters.add(lvl as LogLevel);
        activeFilters = activeFilters; // trigger reactivity
    }

    $: filtered = ALL_LOGS.filter(e => {
        if (activeFilters.size > 0 && !activeFilters.has(e.level)) return false;
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            return e.message.toLowerCase().includes(q) || e.detail.toLowerCase().includes(q);
        }
        return true;
    });

    // ── Pagination ────────────────────────────────────────────────────────────

    let pageSize = '25';
    const PAGE_SIZE_OPTIONS = [
        { value: '10',  label: '10' },
        { value: '25',  label: '25' },
        { value: '50',  label: '50' },
        { value: '100', label: '100' },
    ];
    let page = 1;

    $: numPageSize = parseInt(pageSize);
    $: { page = 1; filtered; pageSize; } // reset to page 1
    $: totalPages  = Math.max(1, Math.ceil(filtered.length / numPageSize));
    $: pageEntries = filtered.slice((page - 1) * numPageSize, page * numPageSize);

    function prev() { if (page > 1) page--; }
    function next() { if (page < totalPages) page++; }

    // ── Export ────────────────────────────────────────────────────────────────

    function exportCSV() {
        const header = 'ID,Level,Timestamp,Message,Detail';
        const rows   = filtered.map(e =>
            [e.id, e.level, e.ts, `"${e.message}"`, `"${e.detail}"`].join(',')
        );
        const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = 'logs.csv';
        a.click();
        URL.revokeObjectURL(url);
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    const ICON_MAP: Record<LogLevel, IconName> = {
        error:   'error',
        warning: 'warning',
        info:    'info',
        success: 'success',
    };

    let formErrors: Record<string, string[]> = { pageSize: [] };
</script>

<div class="page">
    <div class="page-title-row">
        <h1>{$t('nav.logs')}</h1>
        <Button variant="icon" on:click={exportCSV} title={$t('logs.export')} aria-label={$t('logs.export')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
        </Button>
    </div>

    <!-- Log card with search + filters + list -->
    <section class="card log-card">
        <!-- Card toolbar -->
        <div class="card-toolbar">
            <div class="search-wrap">
                <SearchInput bind:value={search} placeholder={$t('logs.search')} />
            </div>
            <FilterChips
                options={LEVEL_OPTIONS}
                active={activeFilters}
                label={$t('logs.filterLabel')}
                on:toggle={e => { toggleFilter(e.detail); activeFilters = activeFilters; }}
            />
        </div>

        {#if pageEntries.length === 0}
            <p class="empty">{$t('logs.empty')}</p>
        {:else}
            <ul class="log-list">
                {#each pageEntries as ev (ev.id)}
                    <li class="log-entry log-entry--{ev.level}">
                        <span class="log-level-badge">
                            <Icon name={ICON_MAP[ev.level]} size={14} />
                            {ev.level}
                        </span>
                        <span class="log-body">
                            <span class="log-message">{ev.message}</span>
                            <span class="log-detail">{ev.detail}</span>
                        </span>
                        <span class="log-ts">{ev.ts}</span>
                    </li>
                {/each}
            </ul>

            <!-- Pagination -->
            <div class="pagination">
                <div class="pagination-settings">
                    <span class="label">{$t('logs.rowsPerPage') || 'Rows per page'}:</span>
                    <div class="page-size-select">
                        <Select 
                            name="pageSize" 
                            bind:value={pageSize} 
                            options={PAGE_SIZE_OPTIONS} 
                            errors={formErrors}
                        />
                    </div>
                </div>

                <div class="pagination-actions">
                    <span class="pagination-info">
                        {(page - 1) * numPageSize + 1}–{Math.min(page * numPageSize, filtered.length)}
                        {$t('logs.of')} {filtered.length}
                    </span>
                    <div class="pagination-controls">
                        <Button variant="icon" disabled={page === 1} on:click={prev}>
                            <Icon name="chevron-left" size={18} />
                        </Button>
                        <Button variant="icon" disabled={page === totalPages} on:click={next}>
                            <Icon name="chevron-right" size={18} />
                        </Button>
                    </div>
                </div>
            </div>
        {/if}
    </section>
</div>

<style lang="scss">
    // ── Page title row ────────────────────────────────────────────────────────

    .page-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.5em;

        h1 {
            font-size: 1.8em;
            font-weight: 300;
            color: $color-primary;
            margin: 0;
        }
    }

    // ── Card toolbar (search + filters) ───────────────────────────────────────

    .card-toolbar {
        display: flex;
        align-items: center;
        gap: 2em;
        padding: 1.25em 1.5em;
        flex-wrap: wrap;

        .search-wrap {
            flex: 1;
            min-width: 240px;
            
            :global(.form-field) { margin-bottom: 0; }
        }
    }

    // ── Log list ──────────────────────────────────────────────────────────────

    .log-card { padding: 0 !important; }

    .log-list {
        list-style: none;
        margin: 0;
        padding: 0;
        border-top: 1px solid rgba($color-border, 0.5);
    }

    .log-entry {
        display: flex;
        align-items: center;
        gap: 1.25em;
        padding: 0.875em 1.5em;
        transition: background-color 0.2s ease;
        position: relative;

        &:hover { 
            background-color: $color-bg-offset; 
        }

        &--error   .log-level-badge { background-color: rgba(#F44336, 0.12); color: #F44336; }
        &--warning .log-level-badge { background-color: rgba(#FFB300, 0.12); color: #FFB300; }
        &--success .log-level-badge { background-color: rgba(#4CAF50, 0.12); color: #4CAF50; }
        &--info    .log-level-badge { background-color: $color-bg-active; color: $color-text-secondary; }

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 1.5em;
            right: 1.5em;
            height: 1px;
            background-color: $color-border;
            opacity: 0.5;
        }

        &:last-child::after {
            display: none;
        }
    }

    .log-level-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5em;
        padding: 0.25em 0.75em;
        border-radius: 100px;
        font-size: 0.65em;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        flex-shrink: 0;
        min-width: 80px;
    }

    .log-body {
        display: flex;
        flex-direction: column;
        gap: 0.15em;
        flex: 1;
        min-width: 0;
    }

    .log-message {
        font-size: 0.9em;
        font-weight: 500;
        color: $color-text;
    }

    .log-detail {
        font-size: 0.82em;
        color: $color-text-secondary;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .log-ts {
        font-size: 0.78em;
        color: $color-disabled-text;
        white-space: nowrap;
        flex-shrink: 0;
        font-feature-settings: "tnum";
        font-variant-numeric: tabular-nums;
    }

    .empty {
        padding: 2em;
        text-align: center;
        color: $color-disabled-text;
        font-size: 0.9em;
    }

    // ── Pagination ─────────────────────────────────────────────────────────────

    .pagination {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 2.5em;
        padding: 0.5em 1.5em; // Reduced vertical padding
        border-top: 1px solid rgba($color-border, 0.5);
        color: $color-text-secondary;
        font-size: 0.85em;
        min-height: 56px; // Material standard for data table footer
    }

    .pagination-settings {
        display: flex;
        align-items: center;
        gap: 0.5em;

        .label {
            white-space: nowrap;
        }

        .page-size-select {
            width: 70px;
            :global(.form-field) { 
                margin-bottom: 0; 
                gap: 0;
            }
            :global(select) { 
                padding: 0.35em 2em 0.35em 0.6em; // More compact but standard padding
                font-size: 0.95em;
                background-color: transparent;
                border: 1px solid transparent;
                border-radius: 4px;
                cursor: pointer;
                color: $color-text;
                font-weight: 600;
                margin: 0;
                background-position: right 0.4em center;
                
                &:hover { 
                    background-color: $color-bg-active;
                    border-color: $color-border;
                }
                &:focus {
                    border-color: $color-primary;
                }
            }
            :global(.field-errors) { display: none; } // Hide errors area in footer
        }
    }

    .pagination-actions {
        display: flex;
        align-items: center;
        gap: 1.5em;
    }

    .pagination-info {
        font-feature-settings: "tnum";
        white-space: nowrap;
    }

    .pagination-controls {
        display: flex;
        align-items: center;
        gap: 0.25em;

        :global(.btn--icon) {
            color: $color-text;
            width: 32px;
            height: 32px;
            
            &:disabled {
                opacity: 0.3;
                color: $color-disabled-text;
            }
        }
    }
</style>
